<template>
  <v-container fluid class="virtual-classroom-page">
    <ClassRoomForm
      v-model="formDialog"
      :classroom="selectedClassroom"
      @saved="getList"
    />

    <ClassroomAssignments
      v-model="assignmentDialog"
      :classroom="selectedClassroom"
      type="student"
      source="virtual"
      @changed="getList"
    />

    <AssignCurriculum
      :editDialog="curriculumDialog"
      :selectedClassRoom="selectedClassroom"
      source="virtual"
      @close="closeCurriculums"
      @submit="curriculumsSaved"
    />

    <TermMaterialTemplates
      :dialog="termTemplatesDialog"
      @close="termTemplatesDialog = false"
    />

    <AssignTermMaterialTemplate
      :dialog="materialDialog"
      :selectedClassRoom="selectedClassroom"
      source="virtual"
      @close="materialDialog = false"
      @saved="materialSaved"
    />

    <ClassroomReportCard
      v-if="reportDialog"
      :ClassRoom="reportClassroom"
      source="virtual"
      @close="closeReportCard"
    />

    <div class="virtual-classroom-heading">
      <div>
        <div class="page-eyebrow">Academic grouping</div>
        <h1>Virtual classrooms</h1>
        <p>Group students at a similar level for curriculum-based grading and report cards.</p>
      </div>

      <div class="heading-actions">
        <v-btn color="primary" outlined @click="termTemplatesDialog = true">
          <v-icon left>mdi-file-document-multiple-outline</v-icon>
          Material templates
        </v-btn>
        <v-btn color="primary" depressed @click="openCreate">
          <v-icon left>mdi-plus</v-icon>
          New classroom
        </v-btn>
      </div>
    </div>

    <v-row class="classroom-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-door-open</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ items.length }}</div>
            <div class="metric-label">Total classrooms</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-account-group-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ studentTotal }}</div>
            <div class="metric-label">Total students</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-book-open-page-variant-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ curriculumTotal }}</div>
            <div class="metric-label">Curriculums assigned</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card :loading="loading" class="classrooms-card" elevation="0">
      <div class="classroom-filters">
        <v-text-field
          v-model="search"
          class="classroom-search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search classrooms, departments, or curriculums"
          single-line
          hide-details
          clearable
        />

        <div class="filter-actions">
          <v-btn
            icon
            outlined
            color="primary"
            class="classroom-action-btn"
            :loading="loading"
            aria-label="Refresh classrooms"
            @click="getList"
          >
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        :headers="headers"
        :items="items"
        :search="search"
        :loading="loading"
        :sort-by="['name']"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        item-key="id"
        loading-text="Loading classrooms..."
        class="classrooms-table"
      >
        <template v-slot:item="{ item }">
          <tr class="classroom-row">
            <td>
              <div class="classroom-identity">
                <div class="classroom-avatar">
                  <v-icon color="primary" size="20">mdi-door-open</v-icon>
                </div>
                <div class="classroom-name-wrap">
                  <div class="classroom-name">{{ item.name }}</div>
                  <div class="classroom-subtitle">
                    {{ count(item) }}
                    {{ count(item) === 1 ? "student" : "students" }}
                  </div>
                </div>
              </div>
            </td>

            <td>
              <div v-if="item.Class" class="detail-line">
                <v-icon color="primary" size="16">mdi-school-outline</v-icon>
                <div>
                  <strong>{{ item.Class.name }}</strong>
                  <span>Department</span>
                </div>
              </div>
              <div v-else class="detail-line muted-detail">
                <v-icon size="16">mdi-school-outline</v-icon>
                <div><strong>Not assigned</strong><span>No department</span></div>
              </div>
            </td>

            <td>
              <div v-if="curriculums(item).length" class="chip-list">
                <v-chip
                  v-for="curriculum in curriculums(item)"
                  :key="curriculum.id"
                  x-small
                  outlined
                  color="primary"
                >
                  {{ curriculum.name }}
                </v-chip>
              </div>
              <span v-else class="status-label warning-status">
                <v-icon color="error" size="14">mdi-book-off-outline</v-icon>
                No curriculum
              </span>
            </td>

            <td class="text-center">
              <button
                type="button"
                class="student-count assignment-count"
                :aria-label="`Manage students in ${item.name}`"
                @click="openAssignments(item)"
              >
                {{ count(item) }}
              </button>
            </td>

            <td>
              <div v-if="materialAssignments(item).length" class="chip-list">
                <v-chip
                  v-for="assignment in materialAssignments(item)"
                  :key="assignment.id"
                  x-small
                  outlined
                  color="primary"
                >
                  {{ materialLabel(assignment) }}
                </v-chip>
              </div>
              <span v-else class="status-label muted-status">
                <v-icon size="14">mdi-file-outline</v-icon>
                Not assigned
              </span>
            </td>

            <td class="text-right">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon aria-label="Classroom actions">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>
                <v-list dense class="menu-button">
                  <v-list-item @click="createReportCard(item)">
                    <v-list-item-icon><v-icon size="19">mdi-file-chart-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Create report card</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="openAssignments(item)">
                    <v-list-item-icon><v-icon size="19">mdi-account-plus-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Assign student</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openCurriculums(item)">
                    <v-list-item-icon><v-icon size="19">mdi-book-open-page-variant-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Manage curriculums</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="openMaterials(item)">
                    <v-list-item-icon><v-icon size="19">mdi-file-document-edit-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Assign materials covered</v-list-item-title>
                  </v-list-item>
                  <v-divider />
                  <v-list-item @click="openEdit(item)">
                    <v-list-item-icon><v-icon size="19">mdi-pencil-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="deleteClassroom(item)">
                    <v-list-item-icon><v-icon color="error" size="19">mdi-delete-outline</v-icon></v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="44" color="grey lighten-1">mdi-door-open</v-icon>
            <strong>No classrooms found</strong>
            <span>Add your first classroom to group students by curriculum.</span>
            <v-btn color="primary" depressed small @click="openCreate">
              <v-icon left small>mdi-plus</v-icon>
              Add classroom
            </v-btn>
          </div>
        </template>

        <template v-slot:no-results>
          <div class="empty-state">
            <v-icon size="44" color="grey lighten-1">mdi-magnify-close</v-icon>
            <strong>No matching classrooms</strong>
            <span>No classrooms matched "{{ search }}".</span>
            <v-btn text color="primary" small @click="search = null">Clear search</v-btn>
          </div>
        </template>
      </v-data-table>

      <v-data-iterator
        v-else
        :items="items"
        :search="search"
        :loading="loading"
        :items-per-page="10"
        :footer-props="{ 'items-per-page-options': [10, 25] }"
        class="mobile-classroom-list"
      >
        <template v-slot:default="{ items: mobileItems }">
          <div v-for="item in mobileItems" :key="item.id" class="mobile-classroom-card">
            <div class="mobile-card-top">
              <div class="classroom-avatar">
                <v-icon color="primary" size="20">mdi-door-open</v-icon>
              </div>
              <div class="mobile-card-identity">
                <strong>{{ item.name }}</strong>
                <span>{{ item.Class ? item.Class.name : "No department" }}</span>
              </div>
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon><v-icon>mdi-dots-horizontal</v-icon></v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="createReportCard(item)"><v-list-item-title>Create report card</v-list-item-title></v-list-item>
                  <v-divider />
                  <v-list-item @click="openAssignments(item)"><v-list-item-title>Assign student</v-list-item-title></v-list-item>
                  <v-list-item @click="openCurriculums(item)"><v-list-item-title>Manage curriculums</v-list-item-title></v-list-item>
                  <v-list-item @click="openMaterials(item)"><v-list-item-title>Assign materials covered</v-list-item-title></v-list-item>
                  <v-divider />
                  <v-list-item @click="openEdit(item)"><v-list-item-title>Edit</v-list-item-title></v-list-item>
                  <v-list-item @click="deleteClassroom(item)"><v-list-item-title class="error--text">Delete</v-list-item-title></v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div class="mobile-classroom-details">
              <div><span>Students</span><strong class="mobile-student-count">{{ count(item) }}</strong></div>
              <div><span>Curriculum</span><strong>{{ curriculumNames(item) || "Not assigned" }}</strong></div>
              <div><span>Materials covered</span><strong>{{ materialNames(item) || "Not assigned" }}</strong></div>
              <div><span>Department</span><strong>{{ item.Class ? item.Class.name : "Not assigned" }}</strong></div>
            </div>

            <div class="mobile-assignment-actions">
              <v-btn text small color="primary" @click="openAssignments(item)">Manage students</v-btn>
            </div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="44" color="grey lighten-1">mdi-door-open</v-icon>
            <strong>No classrooms found</strong>
            <span>Add your first classroom to group students by curriculum.</span>
          </div>
        </template>
      </v-data-iterator>
    </v-card>
  </v-container>
</template>

<script>
import service from "../service.js";
import ClassRoomForm from "./ClassRoomForm.vue";
import ClassroomAssignments from "@/modules/ClassRoom/components/ClassroomAssignments.vue";
import AssignCurriculum from "@/modules/ClassRoom/components/AssignCurriculum.vue";

const TermMaterialTemplates = () =>
  import("@/modules/TermMaterials/components/TermMaterialTemplates.vue");
const AssignTermMaterialTemplate = () =>
  import("@/modules/TermMaterials/components/AssignTermMaterialTemplate.vue");
const ClassroomReportCard = () =>
  import("@/modules/ClassRoom/components/ClassroomReportCard.vue");

export default {
  name: "virtual-classroom-list",
  components: {
    ClassRoomForm,
    ClassroomAssignments,
    AssignCurriculum,
    TermMaterialTemplates,
    AssignTermMaterialTemplate,
    ClassroomReportCard,
  },
  data: () => ({
    loading: false,
    search: null,
    items: [],
    formDialog: false,
    assignmentDialog: false,
    curriculumDialog: false,
    termTemplatesDialog: false,
    materialDialog: false,
    reportDialog: false,
    reportClassroom: null,
    selectedClassroom: null,
    headers: [
      { text: "Classroom", value: "name" },
      { text: "Department", value: "Class.name", width: "220px" },
      { text: "Curriculum", value: "Curriculums", width: "220px", sortable: false },
      { text: "Students", value: "studentCount", align: "center", width: "110px" },
      { text: "Materials covered", value: "TermMaterialAssignments", width: "230px", sortable: false },
      { text: "", value: "actions", sortable: false, align: "end", width: "60px" },
    ],
  }),
  computed: {
    studentTotal() {
      return this.items.reduce((sum, item) => sum + this.count(item), 0);
    },
    curriculumTotal() {
      return this.items.reduce((sum, item) => sum + this.curriculums(item).length, 0);
    },
  },
  created() {
    document.title = "taleem - Virtual Classrooms";
    this.getList();
  },
  methods: {
    count(item) {
      const rows = item.VirtualClassRoomStudents;
      const explicit = item.studentCount;
      return Array.isArray(rows) ? rows.length : Number(explicit || 0);
    },
    curriculums(item) {
      if (Array.isArray(item.Curriculums)) return item.Curriculums;
      return item.Curriculum ? [item.Curriculum] : [];
    },
    curriculumNames(item) {
      return this.curriculums(item).map((curriculum) => curriculum.name).join(", ");
    },
    materialAssignments(item) {
      return Array.isArray(item.TermMaterialAssignments)
        ? item.TermMaterialAssignments.filter((assignment) => assignment.TermMaterialTemplate)
        : [];
    },
    materialLabel(assignment) {
      const template = assignment.TermMaterialTemplate
        ? assignment.TermMaterialTemplate.name
        : "Material";
      return assignment.Term && assignment.Term.name
        ? `${template} (${assignment.Term.name})`
        : template;
    },
    materialNames(item) {
      return this.materialAssignments(item).map(this.materialLabel).join(", ");
    },
    async getList() {
      this.loading = true;
      try {
        const response = await service.getAll();
        this.items = (response.data || []).sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );
      } catch (error) {
        this.$swal({
          title: "Virtual Classroom List",
          text: error && error.data && error.data.message
            ? error.data.message
            : "Unable to load classrooms.",
          icon: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.selectedClassroom = null;
      this.formDialog = true;
    },
    openEdit(item) {
      this.selectedClassroom = item;
      this.formDialog = true;
    },
    openAssignments(item) {
      this.selectedClassroom = item;
      this.assignmentDialog = true;
    },
    openCurriculums(item) {
      this.selectedClassroom = item;
      this.curriculumDialog = true;
    },
    closeCurriculums() {
      this.curriculumDialog = false;
      this.selectedClassroom = null;
    },
    curriculumsSaved() {
      this.curriculumDialog = false;
      this.selectedClassroom = null;
      this.getList();
    },
    openMaterials(item) {
      this.selectedClassroom = item;
      this.materialDialog = true;
    },
    materialSaved() {
      this.materialDialog = false;
      this.getList();
    },
    createReportCard(item) {
      if (!this.curriculums(item).length) {
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "error", title: "No curriculum assigned to this virtual classroom" });
        return;
      }
      if (!this.count(item)) {
        this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000 })
          .fire({ icon: "error", title: "No students found in this virtual classroom" });
        return;
      }
      this.reportClassroom = item;
      this.reportDialog = true;
    },
    closeReportCard() {
      this.reportDialog = false;
      this.reportClassroom = null;
    },
    showPendingFeature(feature) {
      this.$swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3200 }).fire({
        icon: "info",
        title: `${feature} for virtual classrooms will be connected in the next step.`,
      });
    },
    deleteClassroom(item) {
      this.$swal({
        title: "Delete classroom?",
        text: `${item.name} and its assignments will be deleted.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (!result.value) return;
        try {
          await service.delete(item.id);
          this.$swal("Deleted", "Classroom has been deleted.", "success");
          this.getList();
        } catch (error) {
          this.$swal({
            title: "Unable to delete classroom",
            text: error && error.data && error.data.message
              ? error.data.message
              : "Please try again.",
            icon: "error",
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.virtual-classroom-page { min-height: 100%; padding: 30px 36px 48px; background: #f6f8f6; }
.virtual-classroom-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 22px; }
.page-eyebrow { margin-bottom: 5px; color: #43a047; font-size: 11px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
.virtual-classroom-heading h1 { margin: 0; color: #172033; font-size: 29px; line-height: 1.2; letter-spacing: -.025em; }
.virtual-classroom-heading p { margin: 6px 0 0; color: #728095; font-size: 13px; }
.heading-actions { display: flex; align-items: center; gap: 8px; }
.heading-actions .v-btn { height: 42px; border-radius: 9px; text-transform: none; letter-spacing: 0; }
.classroom-metrics { margin: 0 0 10px; }
.metric-item { display: flex; align-items: center; gap: 12px; }
.metric-icon { display: flex; width: 40px; height: 40px; flex: 0 0 40px; align-items: center; justify-content: center; border-radius: 10px; background: #eaf6eb; }
.metric-value { color: #253047; font-size: 18px; font-weight: 750; line-height: 1.2; }
.metric-label { margin-top: 2px; color: #7b8799; font-size: 11px; }
.classrooms-card { overflow: hidden; border: 1px solid #e3e9e3; border-radius: 11px !important; }
.classroom-filters { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 17px 20px; border-bottom: 1px solid #edf0ed; }
.classroom-search { max-width: 470px; }
.classroom-search ::v-deep .v-input__slot { border-radius: 9px !important; }
.filter-actions { display: flex; align-items: center; gap: 12px; }
.classroom-action-btn { flex: 0 0 auto; width: 40px; min-width: 40px !important; height: 40px; }
.classrooms-table ::v-deep th { height: 42px; color: #718075 !important; background: #f8faf8; font-size: 9px; font-weight: 750; letter-spacing: .04em; text-transform: uppercase; }
.classrooms-table ::v-deep td { min-height: 66px; padding-top: 9px; padding-bottom: 9px; color: #425046; border-color: #edf0ed !important; font-size: 12px; }
.classroom-row { transition: background .15s; }
.classroom-row:hover { background: #f7fbf7 !important; }
.classroom-identity { display: flex; align-items: center; gap: 11px; min-width: 185px; }
.classroom-avatar { display: flex; width: 40px; height: 40px; flex: 0 0 40px; align-items: center; justify-content: center; border-radius: 10px; background: #eaf6eb; }
.classroom-name-wrap { min-width: 0; }
.classroom-name { overflow: hidden; color: #253047; font-size: 13px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
.classroom-subtitle { margin-top: 2px; color: #879288; font-size: 10px; }
.detail-line { display: flex; align-items: flex-start; gap: 7px; min-width: 155px; }
.detail-line > div { display: flex; min-width: 0; flex-direction: column; }
.detail-line strong { overflow: hidden; color: #445047; font-size: 11px; font-weight: 650; text-overflow: ellipsis; white-space: nowrap; }
.detail-line span { margin-top: 2px; color: #899489; font-size: 9px; }
.muted-detail strong, .muted-detail span { color: #8a968d; }
.student-count { display: inline-flex; min-width: 34px; height: 30px; padding: 0 9px; align-items: center; justify-content: center; color: #2e7d32; border: 0; border-radius: 8px; background: #eaf6eb; font-size: 16px; font-weight: 800; }
.assignment-count, .assignment-link { cursor: pointer; }
.assignment-count:focus-visible, .assignment-link:focus-visible { outline: 2px solid #43a047; outline-offset: 2px; }
.assignment-link { padding: 0; color: inherit; border: 0; background: transparent; text-align: left; }
.status-label { display: inline-flex; align-items: center; gap: 4px; color: #2e7d32; font-size: 10px; font-weight: 650; }
.muted-status { color: #8a968d; }
.warning-status { color: #c62828; }
.chip-list { display: flex; flex-wrap: wrap; gap: 5px; }
.menu-button .v-list-item__icon { min-width: 30px; margin-right: 4px; }
.empty-state { display: flex; min-height: 220px; padding: 40px 20px; flex-direction: column; align-items: center; justify-content: center; gap: 6px; color: #526057; text-align: center; }
.empty-state span { max-width: 360px; color: #929d95; font-size: 11px; }
.empty-state .v-btn { margin-top: 8px; border-radius: 8px; text-transform: none; letter-spacing: 0; }
.mobile-classroom-list { padding: 8px 12px 0; }
.mobile-classroom-card { padding: 16px 3px; border-bottom: 1px solid #edf0ed; }
.mobile-card-top { display: flex; align-items: center; }
.mobile-card-identity { display: flex; min-width: 0; flex: 1; flex-direction: column; margin-left: 11px; }
.mobile-card-identity strong { overflow: hidden; color: #253047; font-size: 14px; text-overflow: ellipsis; white-space: nowrap; }
.mobile-card-identity span { margin-top: 2px; color: #879288; font-size: 11px; }
.mobile-classroom-details { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 14px 0 0 51px; padding-top: 11px; border-top: 1px solid #f0f2f0; }
.mobile-classroom-details div { display: flex; min-width: 0; flex-direction: column; }
.mobile-classroom-details span { margin-bottom: 3px; color: #919b92; font-size: 10px; text-transform: uppercase; }
.mobile-classroom-details strong { overflow: hidden; color: #445047; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.mobile-classroom-details .mobile-student-count { color: #2e7d32; font-size: 17px; font-weight: 800; }
.mobile-assignment-actions { display: flex; margin: 12px 0 0 43px; gap: 4px; }
.mobile-assignment-actions .v-btn { border-radius: 7px; text-transform: none; letter-spacing: 0; }
@media (max-width: 959px) { .virtual-classroom-page { padding: 22px 14px 36px; } .classroom-filters { align-items: stretch; flex-direction: column; } .classroom-search { width: 100%; max-width: none; } .filter-actions { justify-content: flex-end; } }
@media (max-width: 599px) { .virtual-classroom-page { padding: 20px 14px 36px; } .virtual-classroom-heading { align-items: flex-start; } .virtual-classroom-heading h1 { font-size: 25px; } .virtual-classroom-heading p { max-width: 220px; } .heading-actions .v-btn { min-width: 44px !important; padding: 0 11px !important; } .classroom-metrics { margin-bottom: 8px; } .classroom-metrics > div { padding-top: 6px; padding-bottom: 12px; } .metric-value { font-size: 15px; } .classroom-filters { gap: 12px; padding: 14px; } }
</style>
